import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Connects to data-controller="cascading-filter-value"
// Fetches options with cascade from previous rows; multi-select with shift+click
export default class extends Controller {
  static values = { initialValues: Array }

  connect() {
    this.flatpickrInstance = null
    this.syncRequestId = 0
    this.syncValueInput()
  }

  disconnect() {
    this.syncRequestId += 1
    this.destroyFlatpickr()
  }

  destroyFlatpickr() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy()
      this.flatpickrInstance = null
    }
  }

  columnChanged() {
    this.resetRowValue()
    this.syncValueInput()
    this.refreshSiblingRows({ reset: true })
  }

  selectionChanged() {
    this.refreshSiblingRows({ reset: true })
  }

  getDrawerData(key) {
    const drawer = this.element.closest("[data-controller*='filter-drawer']")
    if (!drawer) return null
    const attr = drawer.getAttribute(`data-filter-drawer-${key}-value`)
    if (!attr) return null
    try {
      return JSON.parse(attr)
    } catch {
      return attr
    }
  }

  getOptionsUrl() {
    const url = this.getDrawerData("options-url")
    return typeof url === "string" ? url : null
  }

  getDropdownColumns() {
    const cols = this.getDrawerData("dropdown-columns")
    return Array.isArray(cols) ? cols : []
  }

  getCascadeDropdownColumns() {
    const cols = this.getDrawerData("cascade-dropdown-columns")
    return Array.isArray(cols) ? cols : this.getDropdownColumns()
  }

  getValueInputName() {
    const row = this.element.closest("[data-cascade-filter-row]")
    const colSelect = row?.querySelector("[data-cascading-filter-column]")
    const name = colSelect?.getAttribute("name")
    if (!name) return "filter[0][value][]"
    return name.replace(/\[column\]$/, "[value][]")
  }

  getCascadeFromPreviousRows() {
    const rows = this.element.closest("[data-filter-drawer-target='cascadeRows']")?.querySelectorAll("[data-cascade-filter-row]") || []
    const cascade = []
    const myRow = this.element.closest("[data-cascade-filter-row]")
    for (const row of rows) {
      if (row === myRow) break
      const colSelect = row.querySelector("[data-cascading-filter-column]")
      const column = colSelect?.value
      if (!column) continue
      const values = this.getRowValues(row)
      if (values.length > 0) {
        cascade.push({ column, value: values.length === 1 ? values[0] : values })
      }
    }
    return cascade
  }

  getRowValues(row) {
    const textInput = row.querySelector("[data-cascading-filter-text]")
    if (textInput?.value) return [textInput.value]

    const valueSelect = row.querySelector("[data-cascading-filter-select]")
    if (valueSelect) {
      return Array.from(valueSelect.selectedOptions).map((opt) => opt.value).filter(Boolean)
    }

    const checked = row.querySelectorAll(".cascade-option:checked")
    if (checked.length) {
      return Array.from(checked).map((cb) => cb.value).filter(Boolean)
    }

    const inputs = row.querySelectorAll("[data-cascading-value-input]")
    if (inputs.length) {
      return Array.from(inputs).map((inp) => inp.value).filter(Boolean)
    }

    // During initial reconnect, previous rows may still be rendering their controls.
    // Fall back to that row controller's persisted initial values from URL filters.
    try {
      const ctrl = this.application.getControllerForElementAndIdentifier(row, "cascading-filter-value")
      if (ctrl?.hasInitialValuesValue && Array.isArray(ctrl.initialValuesValue)) {
        return ctrl.initialValuesValue.filter(Boolean).map((v) => String(v))
      }
    } catch (_) {}

    return []
  }

  async syncValueInput() {
    const requestId = ++this.syncRequestId
    const valueCell = this.element.querySelector("[data-cascading-filter-value-cell]")
    const columnSelect = this.element.querySelector("[data-cascading-filter-column]")
    if (!valueCell || !columnSelect) return

    const column = columnSelect.value
    const columnTypes = this.getDrawerData("column-types") || {}
    const isDatetime = String(columnTypes[column] || "") === "datetime"
    const dropdownColumns = this.getCascadeDropdownColumns()
    const optionsUrl = this.getOptionsUrl()
    const isDropdownColumn = !isDatetime && column && dropdownColumns.includes(column) && optionsUrl

    if (!column) {
      valueCell.innerHTML = '<div class="text-sm text-base-content/50 py-2">Select column first</div>'
      return
    }

    if (!isDropdownColumn) {
      this.destroyFlatpickr()
      const baseName = this.getValueInputName().replace(/\[\]$/, "")
      const currentVal = this.getCurrentValues(valueCell)
      const textValue = currentVal.length > 0 ? currentVal[0] : ""
      const placeholder = isDatetime ? "Select date range..." : "Value..."
      valueCell.innerHTML = `<input type="text" name="${this.escapeHtml(baseName)}" value="${this.escapeHtml(textValue)}" placeholder="${this.escapeHtml(placeholder)}" class="input input-bordered input-sm w-full h-9" data-cascading-filter-text />`
      const textInput = valueCell.querySelector("[data-cascading-filter-text]")
      textInput?.addEventListener("input", () => this.refreshSiblingRows({ reset: true }))

      if (isDatetime && textInput) {
        this.flatpickrInstance = flatpickr(textInput, {
          mode: "range",
          dateFormat: "Y-m-d",
          defaultDate: textValue ? (textValue.includes(" to ") ? textValue.split(/\s+to\s+/) : textValue) : undefined,
          allowInput: true,
          disableMobile: false,
          static: true,
          position: "auto right",
          onChange: () => this.refreshSiblingRows({ reset: true }),
          onClose: () => this.refreshSiblingRows({ reset: true })
        })
      }
      return
    }

    this.destroyFlatpickr()
    valueCell.innerHTML = '<span class="loading loading-spinner loading-sm"></span>'

    try {
      const cascade = this.getCascadeFromPreviousRows()
      const url = new URL(optionsUrl, window.location.origin)
      url.searchParams.set("column", column)
      url.searchParams.set("cascade", JSON.stringify(cascade))

      const res = await fetch(url.toString(), { headers: { "Accept": "application/json" } })
      const values = await res.json()
      if (requestId !== this.syncRequestId) return

      const normalizedOptions = (Array.isArray(values) ? values : []).map((entry) => {
        if (entry && typeof entry === "object" && !Array.isArray(entry)) {
          const label = String(entry.label ?? entry.value ?? "")
          const value = String(entry.value ?? entry.label ?? "")
          const isBlank = Boolean(entry.is_blank)
          const displayLabel = isBlank ? "Blank" : label
          return { label, value, isBlank, displayLabel }
        }
        const scalar = String(entry ?? "")
        return { label: scalar, value: scalar, isBlank: false, displayLabel: scalar }
      })
      const currentValues = this.getCurrentValues(valueCell)

      const baseName = this.getValueInputName().replace(/\[\]$/, "")

      const optionsHtml = normalizedOptions.map((opt, i) => {
        const label = String(opt.label ?? "")
        const val = String(opt.value ?? label)
        const displayLabel = String(opt.displayLabel ?? label)
        const escapedValue = this.escapeHtml(val)
        const escapedLabel = this.escapeHtml(label)
        const escapedDisplayLabel = this.escapeHtml(displayLabel)
        const checked = currentValues.includes(val) || currentValues.includes(label)
        return `<li class="text-sm" data-cascading-option data-option-index="${i}">
          <label class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer select-none hover:bg-base-300 min-h-0">
            <input type="checkbox" class="checkbox checkbox-sm cascade-option" ${checked ? "checked" : ""} value="${escapedValue}" data-cascading-checkbox data-cascading-label="${escapedDisplayLabel}" />
            ${opt.isBlank ? "" : `<span>${escapedLabel}</span>`}
          </label>
        </li>`
      }).join("")

      const displayText = this.selectedDisplayText(currentValues, normalizedOptions)
      const hiddenInputs = currentValues.map((v) => `<input type="hidden" name="${this.escapeHtml(baseName)}[]" value="${this.escapeHtml(v)}" data-cascading-value-input />`).join("")

      const dropdownHtml = `
        <details class="dropdown dropdown-end w-full group" data-cascading-filter-dropdown>
          <summary class="select select-bordered select-sm w-full h-9 min-h-9 cursor-pointer flex items-center justify-between gap-2 list-none [&::-webkit-details-marker]:hidden" data-cascading-filter-trigger>
            <span class="truncate">${this.escapeHtml(displayText)}</span>            
          </summary>
          <ul class="dropdown-content bg-base-100 rounded-box z-50 w-full max-h-60 overflow-y-auto p-1 mt-1 shadow-lg border border-base-300 text-sm" data-cascading-filter-menu>
            ${optionsHtml}
          </ul>
          ${hiddenInputs}
        </details>`
      valueCell.innerHTML = dropdownHtml

      const dropdown = valueCell.querySelector("[data-cascading-filter-dropdown]")
      const menu = valueCell.querySelector("[data-cascading-filter-menu]")
      const options = valueCell.querySelectorAll("[data-cascading-option]")
      const checkboxes = valueCell.querySelectorAll("[data-cascading-checkbox]")
      const trigger = valueCell.querySelector("[data-cascading-filter-trigger]")

      const updateDisplay = () => {
        const checked = valueCell.querySelectorAll(".cascade-option:checked")
        const vals = Array.from(checked).map((cb) => cb.value)
        const labels = Array.from(checked).map((cb) => cb.dataset.cascadingLabel || cb.value)
        const hiddenInputs = valueCell.querySelectorAll("[data-cascading-value-input]")
        hiddenInputs.forEach((el) => el.remove())
        vals.forEach((v) => {
          const inp = document.createElement("input")
          inp.type = "hidden"
          inp.name = `${baseName}[]`
          inp.value = v
          inp.setAttribute("data-cascading-value-input", "")
          valueCell.appendChild(inp)
        })
        const span = trigger?.querySelector("span")
        if (span) span.textContent = vals.length > 0 ? labels.join(", ") : "Select..."
      }

      let lastClickedIndex = -1
      options.forEach((opt, i) => {
        const label = opt.querySelector("label")
        const checkbox = opt.querySelector("[data-cascading-checkbox]")
        if (!checkbox) return
        label?.addEventListener("mousedown", (e) => {
          if (e.shiftKey && lastClickedIndex >= 0) {
            e.preventDefault()
            const lo = Math.min(lastClickedIndex, i)
            const hi = Math.max(lastClickedIndex, i)
            const newChecked = !checkbox.checked
            checkboxes.forEach((cb, idx) => {
              if (idx >= lo && idx <= hi) cb.checked = newChecked
            })
            lastClickedIndex = i
            updateDisplay()
            this.refreshSiblingRows({ reset: true })
          } else {
            lastClickedIndex = i
          }
        })
        checkbox.addEventListener("change", () => {
          updateDisplay()
          this.refreshSiblingRows({ reset: true })
        })
      })

      dropdown?.addEventListener("toggle", () => {
        if (dropdown.open) this.refreshSiblingRows()
      })

      document.addEventListener("click", (e) => {
        if (dropdown?.open && !valueCell.contains(e.target)) {
          dropdown.open = false
        }
      })
    } catch {
      if (requestId !== this.syncRequestId) return
      valueCell.innerHTML = '<div class="text-sm text-error py-2">Failed to load options</div>'
    }
  }

  getCurrentValues(valueCell) {
    const textInput = valueCell?.querySelector("[data-cascading-filter-text]")
    if (textInput) return textInput.value ? [textInput.value] : []
    const selectEl = valueCell?.querySelector("[data-cascading-filter-select]")
    if (selectEl) return Array.from(selectEl.selectedOptions).map((opt) => opt.value).filter(Boolean)
    const checkboxes = valueCell?.querySelectorAll(".cascade-option:checked")
    if (checkboxes?.length) return Array.from(checkboxes).map((cb) => cb.value).filter(Boolean)
    const inputs = valueCell?.querySelectorAll("[data-cascading-value-input]")
    if (inputs?.length) return Array.from(inputs).map((inp) => inp.value).filter(Boolean)
    return this.hasInitialValuesValue ? this.initialValuesValue : []
  }

  resetRowValue() {
    const valueCell = this.element.querySelector("[data-cascading-filter-value-cell]")
    if (!valueCell) return

    valueCell.querySelectorAll("[data-cascading-filter-text]").forEach((input) => { input.value = "" })
    valueCell.querySelectorAll("[data-cascading-filter-select]").forEach((select) => { select.selectedIndex = -1 })
    valueCell.querySelectorAll(".cascade-option").forEach((checkbox) => { checkbox.checked = false })
    valueCell.querySelectorAll("[data-cascading-value-input]").forEach((input) => input.remove())

    if (this.hasInitialValuesValue) this.initialValuesValue = []
  }

  refreshSiblingRows({ reset = false } = {}) {
    const container = this.element.closest("[data-filter-drawer-target='cascadeRows']")
    if (!container) return
    const rows = container.querySelectorAll("[data-cascade-filter-row]")
    for (const row of rows) {
      if (row === this.element) continue
      if (!(this.element.compareDocumentPosition(row) & Node.DOCUMENT_POSITION_FOLLOWING)) continue
      try {
        const ctrl = this.application.getControllerForElementAndIdentifier(row, "cascading-filter-value")
        if (!ctrl || typeof ctrl.syncValueInput !== "function") continue
        if (reset && typeof ctrl.resetRowValue === "function") ctrl.resetRowValue()
        ctrl.syncValueInput()
      } catch (_) {}
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }

  selectedDisplayText(currentValues, options) {
    if (!currentValues || currentValues.length === 0) return "Select..."

    const labelByValue = new Map(options.map((opt) => [opt.value, opt.displayLabel || opt.label]))
    const labels = currentValues.map((v) => labelByValue.get(v) || v).filter(Boolean)
    return labels.length > 0 ? labels.join(", ") : "Select..."
  }
}
